import React, { ComponentProps, Fragment, useState } from 'react';
import { asWidgetProps, BucketFormikInput } from 'Src/types/widget-helpers';
import { UnmountClosed } from 'react-collapse';
import domainsService from '@clubmed/domains-service';
import { ICONS } from 'Components/utils/icon/icons';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { classnames } from 'Utils/classnames/classnames.utils';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ADDRESS_FIELDS_CLASSES } from 'Components/widgets/contact-info/constants';
import type { AddressValidatorsType } from './UserAddressForm.utils';
import { fieldsSort } from './UserAddressForm.utils';
const MAIN_FIELDS_NAMES = ['streetNumber', 'streetName', 'zipCode', 'city', 'stateOrDistrict'];
const IGNORED_FIELDS = ['countryCode', 'health_pass_id'];
type InputProps = {
  autoComplete?: string;
  fieldName: string;
  fieldsValues: Record<string, string>;
  hideErrorMessage?: boolean;
  isCountry?: boolean;
  fetchCountriesFromApi?: boolean;
  countriesData?: Array<{
    value: string;
    name: string;
    phoneCode?: string;
  }>;
  isLoadingCountries?: boolean;
  namePrefix?: string;
  onKeyDown?: (e: KeyboardEvent) => void;
  required: boolean | null | undefined;
  options?: Array<{
    name: string;
    value: string;
  }>;
  validator?: () => boolean;
};
const AddressInput = ({
  autoComplete,
  fieldName,
  fieldsValues,
  options,
  hideErrorMessage = false,
  isCountry = false,
  fetchCountriesFromApi = false,
  countriesData,
  isLoadingCountries = false,
  namePrefix = '',
  onKeyDown,
  required,
  validator,
}: InputProps) => {
  const { locale } = useAppContext();
  let computedOptions = options || [];
  if (isCountry) {
    // Get locale-specific countries from domainsService
    const getLocaleSpecificCountries = () => {
      return domainsService.getLocaleCountries(locale).map(({ code, name }) => ({
        value: code,
        name,
      }));
    };
    if (fetchCountriesFromApi && countriesData) {
      // Get locale countries for top of the list
      const localeCountries = getLocaleSpecificCountries();
      const localeCountryCodes = new Set(localeCountries.map((c) => c.value));

      // Filter API countries to exclude locale countries
      const otherCountries = countriesData.filter(
        (country) => !localeCountryCodes.has(country.value),
      );

      // Add separator between locale and other countries
      const separator = {
        value: '',
        name: '─────────────────────────',
        isDisabled: true,
      };

      // Combine: locale countries + separator + other countries from API
      computedOptions = [...localeCountries, separator, ...otherCountries];
    } else if (!fetchCountriesFromApi) {
      // Use countries from domainsService only
      computedOptions = getLocaleSpecificCountries();
    }
  }
  const shouldValidate = required || (validator && fieldsValues?.[fieldName] !== '');
  if (fetchCountriesFromApi && isCountry && isLoadingCountries) {
    return (
      <div className={classnames('p-10', ADDRESS_FIELDS_CLASSES[fieldName] || 'w-full')}>
        <BucketFormikInput
          {...asWidgetProps<ComponentProps<typeof BucketFormikInput>>({
            autoComplete: autoComplete,
            hideErrorMessage: hideErrorMessage,
            isDisabled: true,
            isRequired: !!required,
            label: t(`common.participants.address.${fieldName}`),
            name: `${namePrefix}${fieldName}`,
            onKeyDown: onKeyDown,
            options: [],
            placeholder: t('common.participants.address.selectCountry.label'),
            type: 'select',
            validate: shouldValidate ? validator : false,
          })}
        />
      </div>
    );
  }
  return (
    <div className={classnames('p-10', ADDRESS_FIELDS_CLASSES[fieldName] || 'w-full')}>
      <BucketFormikInput
        {...asWidgetProps<ComponentProps<typeof BucketFormikInput>>({
          autoComplete: autoComplete,
          hideErrorMessage: hideErrorMessage,
          isDisabled: computedOptions.length === 1,
          isRequired: !!required,
          label: t(`common.participants.address.${fieldName}`),
          name: `${namePrefix}${fieldName}`,
          onKeyDown: onKeyDown,
          options: computedOptions,
          placeholder: isCountry ? t('common.participants.address.selectCountry.label') : undefined,
          type: computedOptions?.length ? 'select' : 'text',
          validate: shouldValidate ? validator : false,
        })}
      />
    </div>
  );
};
type Props = {
  fields: Array<ValidationSchema>;
  fieldsValues: Record<string, string>;
  hideErrorMessage?: boolean;
  fetchCountriesFromApi?: boolean;
  countriesData?: Array<{
    value: string;
    name: string;
    phoneCode?: string;
  }>;
  isLoadingCountries?: boolean;
  namePrefix: string;
  onKeyDown?: (e: KeyboardEvent) => void;
  validators?: AddressValidatorsType | null | undefined;
};
const AdditionalAddress = ({
  hideErrorMessage = false,
  isExpanded,
  additionnalFields,
  setIsExpanded,
  fieldsValues,
  namePrefix,
  onKeyDown,
  validators,
}) => (
  <Fragment>
    <UnmountClosed
      {...asWidgetProps<ComponentProps<typeof UnmountClosed>>({
        isOpened: isExpanded,
        theme: {
          collapse: 'collapse-transition w-full',
          content: 'pt-20',
        },
      })}
    >
      {additionnalFields.map(({ name: fieldName, required }) => (
        <AddressInput
          key={fieldName}
          {...asWidgetProps<ComponentProps<typeof AddressInput>>({
            autoComplete: 'nope',
            fieldName: fieldName,
            fieldsValues: fieldsValues,
            hideErrorMessage: hideErrorMessage,
            namePrefix: namePrefix,
            onKeyDown: onKeyDown,
            required: required,
            validator: validators?.[fieldName],
          })}
        />
      ))}
    </UnmountClosed>
    <button
      data-testid="participants-collapsable"
      className="ms-auto flex items-center gap-5 p-10 pb-0 text-14 text-black underline"
      onClick={() => setIsExpanded(!isExpanded)}
      type="button"
    >
      {isExpanded
        ? t('common.close')
        : t('common.participants.address.additionalInformationLegend')}
      <Icon
        {...asWidgetProps<ComponentProps<typeof Icon>>({
          name: ICONS.ARROW_DOWN,
          rotation: isExpanded ? 180 : 0,
          width: '0.5em',
        })}
      />
    </button>
  </Fragment>
);
export const UserAddressForm = ({
  fields,
  fieldsValues,
  hideErrorMessage = false,
  fetchCountriesFromApi = false,
  countriesData: countriesDataProp,
  isLoadingCountries: isLoadingCountriesProp = false,
  namePrefix,
  onKeyDown,
  validators,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const sortedFields = fields.sort(fieldsSort);
  const mainFields = sortedFields.filter(
    (f) => (MAIN_FIELDS_NAMES.includes(f.name) || f.required) && !IGNORED_FIELDS.includes(f.name),
  );
  const additionnalFields = sortedFields.filter(
    (f) => !MAIN_FIELDS_NAMES.includes(f.name) && !IGNORED_FIELDS.includes(f.name) && !f.required,
  );
  const hasAdditionalAddress = additionnalFields.length > 0;
  return (
    <Fragment>
      {mainFields.map<React.ReactNode | null | undefined>(
        ({ name: fieldName, required, values }) => {
          return (
            <AddressInput
              key={fieldName}
              {...asWidgetProps<ComponentProps<typeof AddressInput>>({
                ...(fieldName === 'streetNumber'
                  ? {
                      autoComplete: 'nope',
                    }
                  : null),
                fieldName: fieldName,
                fieldsValues: fieldsValues,
                options: values?.map(({ code, label }) => ({
                  name: label,
                  value: code,
                })),
                hideErrorMessage: hideErrorMessage,
                namePrefix: namePrefix,
                onKeyDown: onKeyDown,
                required: required,
                validator: validators?.[fieldName],
              })}
            />
          );
        },
      )}
      {hasAdditionalAddress && (
        <AdditionalAddress
          {...asWidgetProps<ComponentProps<typeof AdditionalAddress>>({
            isExpanded: isExpanded,
            additionnalFields: additionnalFields,
            setIsExpanded: setIsExpanded,
            fieldsValues: fieldsValues,
            namePrefix: namePrefix,
            onKeyDown: onKeyDown,
            validators: validators,
          })}
        />
      )}
      <AddressInput
        {...asWidgetProps<ComponentProps<typeof AddressInput>>({
          fieldName: 'countryCode',
          fieldsValues: fieldsValues,
          isCountry: true,
          fetchCountriesFromApi: fetchCountriesFromApi,
          countriesData: countriesDataProp,
          isLoadingCountries: isLoadingCountriesProp,
          namePrefix: namePrefix,
          required: sortedFields.find((f) => f.name === 'countryCode')?.required,
          validator: validators?.countryCode,
        })}
      />
    </Fragment>
  );
};
